export type CatalogTranslation = {
  key: string;
  locale: string;
  value?: string | null;
};

export type OptionTranslation = {
  name: string;
  label?: string | null;
  values: { value: string; label?: string | null }[];
};

export type ProductTranslations = {
  productId: string;
  translations: CatalogTranslation[];
  variants: { variantId: string; translations: CatalogTranslation[] }[];
  options?: OptionTranslation[];
};

/** The fields of an order line the portal shows as its name and options. */
export type LocalizableLineItem = {
  title: string;
  product?: {
    productId?: string | null;
    translations?: CatalogTranslation[] | null;
  } | null;
  variant?: {
    variant_id?: string | null;
    option1?: string | null;
    option2?: string | null;
    option3?: string | null;
  } | null;
};

const OPTION_KEYS = ["option1", "option2", "option3"] as const;

function baseLanguage(locale: string): string {
  return locale.split("-")[0].toLowerCase();
}

/** Shopify ids come either numeric or as a GID: compare their last segment. */
function sameId(a: string | null | undefined, b: string | null | undefined) {
  if (!a || !b) return false;
  return String(a).split("/").pop() === String(b).split("/").pop();
}

function findOptions(
  products: ProductTranslations[] | null | undefined,
  productId: string | number | null | undefined,
) {
  return products?.find((p) => sameId(p.productId, String(productId ?? "")))
    ?.options;
}

/** Shopify option name ("Couleur") for the locale; the raw name without one. */
export function localizeOptionName(
  products: ProductTranslations[] | null | undefined,
  productId: string | number | null | undefined,
  name: string,
): string {
  return (
    findOptions(products, productId)?.find((o) => o.name === name)?.label ||
    name
  );
}

/** Shopify option value ("Rouge") for the locale; the raw value without one. */
export function localizeOptionValue(
  products: ProductTranslations[] | null | undefined,
  productId: string | number | null | undefined,
  name: string,
  value: string,
): string {
  return (
    findOptions(products, productId)
      ?.find((o) => o.name === name)
      ?.values.find((v) => v.value === value)?.label || value
  );
}

function pick(
  translations: CatalogTranslation[] | null | undefined,
  key: string,
  language: string,
): string | null {
  const match = translations?.find(
    (t) => t.key === key && !!t.value && baseLanguage(t.locale) === language,
  );
  return match?.value ?? null;
}

/** Product name for the locale; `fallback` without a published translation. */
export function localizeProductTitle(
  products: ProductTranslations[] | null | undefined,
  productId: string | number | null | undefined,
  locale: string,
  fallback: string,
): string {
  const product = products?.find((p) =>
    sameId(p.productId, String(productId ?? "")),
  );
  return pick(product?.translations, "title", baseLanguage(locale)) ?? fallback;
}

/**
 * Returns the line with its product name and variant options in `locale`,
 * BAB-3184. The order snapshot keeps them in the shop's primary language.
 * `fetched` holds the translations resolved for that locale; the ones stored
 * on the line at order load (the customer's locale) are the fallback. Anything
 * without a published translation keeps its original value.
 */
export function localizeLineItem<T extends LocalizableLineItem>(
  item: T,
  locale: string,
  fetched?: ProductTranslations[] | null,
): T {
  const language = baseLanguage(locale);
  if (!language) return item;

  const product = fetched?.find((p) =>
    sameId(p.productId, item.product?.productId),
  );
  const variantTranslations = product?.variants.find((v) =>
    sameId(v.variantId, item.variant?.variant_id),
  )?.translations;

  const title =
    pick(product?.translations, "title", language) ??
    pick(item.product?.translations, "title", language) ??
    item.title;

  let variant = item.variant;
  if (item.variant) {
    const options: Partial<Record<(typeof OPTION_KEYS)[number], string>> = {};
    OPTION_KEYS.forEach((key, index) => {
      const raw = item.variant?.[key];
      if (!raw) return;
      // Option values first: they are what the exchange picker shows, and the
      // legacy variant `optionN` keys can disagree with them (prod: "38/40"
      // value vs "36/38" variant). `options` follows Shopify position order,
      // so option N is the Nth entry.
      const translated =
        product?.options?.[index]?.values.find((v) => v.value === raw)?.label ??
        pick(variantTranslations, key, language);
      if (translated) options[key] = translated;
    });
    if (Object.keys(options).length > 0) {
      variant = { ...item.variant, ...options };
    }
  }

  if (title === item.title && variant === item.variant) return item;
  return { ...item, title, variant };
}
